import type { Bucket } from '../../lib/taskReport';

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * 依存を増やさないためのインラインSVG棒グラフ。
 * 記録が無い区間は薄いグレーの土台だけを描き、0と区別できるようにする。
 *
 * zeroBased=false のときは軸をデータ範囲に合わせる。体重や睡眠時間のように
 * 0から遠い値を0起点で描くと、差がまったく見えなくなるため。
 * 誤読を避けるよう、軸の下端と上端を必ず表示する。
 */
export function BarChart({
  buckets,
  unit,
  target,
  zeroBased = true,
  height = 160,
}: {
  buckets: Bucket[];
  unit?: string;
  target?: number | null;
  zeroBased?: boolean;
  height?: number;
}) {
  const values = buckets.map((b) => b.value).filter((v): v is number => v !== null);
  const dataMax = values.length > 0 ? Math.max(...values) : 0;
  const dataMin = values.length > 0 ? Math.min(...values) : 0;

  let lo = 0;
  let hi = Math.max(dataMax, target ?? 0, 1);
  if (!zeroBased && values.length > 0) {
    const pad = Math.max((dataMax - dataMin) * 0.15, Math.abs(dataMax) * 0.01, 0.5);
    lo = Math.min(dataMin - pad, target ?? Infinity);
    hi = Math.max(dataMax + pad, target ?? -Infinity);
    if (hi - lo < 1e-6) hi = lo + 1;
  }
  const span = hi - lo;

  const n = buckets.length;
  const W = 100;
  const gap = n > 40 ? 0.15 : n > 15 ? 0.6 : 1.6;
  const bw = (W - gap * (n - 1)) / n;
  const plot = height - 22;
  const y = (v: number) => plot - ((v - lo) / span) * plot;

  return (
    <div className="chart-wrap">
      <svg
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
        className="chart"
        role="img"
        aria-label="推移グラフ"
      >
        {target != null && target >= lo && target <= hi && (
          <line
            x1="0"
            x2={W}
            y1={y(target)}
            y2={y(target)}
            className="chart-target"
            vectorEffect="non-scaling-stroke"
          />
        )}
        {buckets.map((b, i) => {
          const x = i * (bw + gap);
          const top = b.value === null ? plot : Math.min(y(b.value), plot - 1.5);
          return (
            <g key={b.from}>
              <rect x={x} y={0} width={bw} height={plot} className="chart-slot" />
              {b.value !== null && (
                <rect x={x} y={top} width={bw} height={plot - top} className="chart-bar">
                  <title>
                    {b.label}: {fmt(b.value)}
                    {unit ?? ''}
                  </title>
                </rect>
              )}
            </g>
          );
        })}
      </svg>
      <div className="chart-axis" style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}>
        {buckets.map((b, i) => (
          <span key={b.from} className="chart-tick">
            {n <= 14 || i % Math.ceil(n / 8) === 0 ? b.label : ''}
          </span>
        ))}
      </div>
      <div className="chart-hi muted">
        {fmt(hi)}
        {unit ?? ''}
      </div>
      <div className="chart-lo muted">
        {fmt(lo)}
        {unit ?? ''}
        {!zeroBased && lo !== 0 && <span className="chart-note">（軸は0起点ではありません）</span>}
      </div>
    </div>
  );
}
